/**
 * Parsing and formatting for the address lists stored on `booking_emails`
 * (`recipients`, `copy_recipients`, `blind_copy_recipients`, `reply_to`).
 *
 * These columns hold delimited text rather than structured data, and historically
 * three writers used three different delimiters: '; ' from the Gmail ingest,
 * ',' from `updateMessageId`, ', ' from the accounts email. Readers split on a
 * single fixed delimiter, so a list written by one path was mis-read by another —
 * a thread with several TO addresses collapsed to one entry, which silently
 * dropped recipients from replies and stopped the multi-recipient warning firing.
 *
 * Everything that reads one of those columns should go through `parseAddressList`,
 * and everything that writes one should go through `formatAddressList`, so the
 * delimiter stops being something each call site has to know about.
 */

export interface EmailAddress {
  /** Display name, unquoted and unescaped. Null when the list held a bare address. */
  name: string | null
  address: string
}

/** Delimiter for newly written lists. Readers accept ',' and ';' regardless. */
const SEPARATOR = '; '

/** A whole token is an address: no spaces, no delimiters, one '@', a dotted domain. */
const BARE_ADDRESS = /^[^\s<>@",;]+@[^\s<>@",;]+\.[^\s<>@",;]+$/

/** The same shape, found anywhere inside a token. */
const EMBEDDED_ADDRESS = /[^\s<>@",;]+@[^\s<>@",;]+\.[^\s<>@",;]+/

/** Leftover header labels that appear inline in some legacy rows ("CC: <a@b.com>"). */
const HEADER_PREFIX = /^(?:to|cc|bcc|reply-to)\s*:\s*/i

/**
 * The local snapshot everyone develops against has `.invalid` appended to every address
 * as an anti-send measure, so `kate@example.com` is stored as `kate@example.com.invalid`
 * and cannot be mailed by accident. Production carries no such suffix.
 *
 * Ignore it when *comparing* addresses — otherwise a client looks like a stranger on
 * their own thread, and our own staff look external, on every developer machine. Never
 * strip it from an address that is about to be sent to, stored, or shown as the
 * destination: that undoes the guard and turns a deliberately dead address back into a
 * live one. The same trap is documented at apps/partner-api/server/messaging/rules.ts.
 */
const forComparison = (value: string): string => value.trim().toLowerCase().replace(/\.invalid$/, '')

/** Display-name characters that force quoting on the way out (RFC 5322 specials). */
const NAME_NEEDS_QUOTING = /[,;<>@"()\\[\]:.]/

/**
 * Split an address list on ',' and ';', ignoring delimiters inside quoted display
 * names, angle-bracketed addresses and parenthesised comments. This is what lets
 * `"Masheder, Scott" <scott@example.com>` survive as one entry.
 *
 * RFC 5322 group syntax ("Team: a@x.com, b@y.com;") is not preserved — the group
 * terminator is treated as a plain delimiter. Groups are expanded by the parser at
 * ingest, so they do not reach these columns.
 */
const splitAddressList = (input: string): string[] => {
  const tokens: string[] = []
  let current = ''
  let inQuotes = false
  let inAngle = false
  let commentDepth = 0

  for (let index = 0; index < input.length; index++) {
    const char = input[index]

    // A backslash escapes the next character inside quotes and comments.
    if ((inQuotes || commentDepth > 0) && char === '\\' && index + 1 < input.length) {
      current += char + input[index + 1]
      index++
      continue
    }

    if (inQuotes) {
      if (char === '"') inQuotes = false
      current += char
      continue
    }

    if (commentDepth > 0) {
      if (char === '(') commentDepth++
      else if (char === ')') commentDepth--
      current += char
      continue
    }

    if (char === '"') {
      inQuotes = true
      current += char
      continue
    }

    if (char === '(') {
      commentDepth = 1
      current += char
      continue
    }

    if (char === '<') inAngle = true
    else if (char === '>') inAngle = false
    else if (!inAngle && (char === ',' || char === ';')) {
      tokens.push(current)
      current = ''
      continue
    }

    current += char
  }

  tokens.push(current)
  return tokens
}

/** Strip surrounding quotes and unescape a display name. */
const cleanDisplayName = (value: string): string => {
  let name = value.trim()

  if (name.length > 1 && name.startsWith('"') && name.endsWith('"')) {
    name = name.slice(1, -1).replace(/\\(.)/g, '$1')
  }

  return name.replace(/\s+/g, ' ').trim()
}

/** Parse a single token. Returns null when no address can be recovered from it. */
const parseAddress = (token: string): EmailAddress | null => {
  const trimmed = token.trim().replace(HEADER_PREFIX, '').trim()
  if (!trimmed) return null

  const angled = trimmed.match(/<([^<>]*)>/)

  if (angled) {
    const address = angled[1].trim()
    if (!BARE_ADDRESS.test(address)) return null

    const name = cleanDisplayName(trimmed.slice(0, angled.index))
    return { name: name || null, address }
  }

  // No angle brackets: a bare address, possibly trailed by a comment.
  const withoutComments = trimmed.replace(/\([^)]*\)/g, '').trim()
  if (BARE_ADDRESS.test(withoutComments)) {
    return { name: null, address: withoutComments }
  }

  // Last resort for malformed tokens — recover anything address-shaped.
  const embedded = withoutComments.match(EMBEDDED_ADDRESS)
  return embedded ? { name: null, address: embedded[0] } : null
}

/**
 * Parse a stored address list into its entries, dropping anything with no
 * recoverable address. Accepts ',' and ';' delimited input, so it reads lists
 * written by any of the historical writers.
 */
export const parseAddressList = (value: string | null | undefined): EmailAddress[] => {
  if (!value) return []

  return splitAddressList(value)
    .map(parseAddress)
    .filter((entry): entry is EmailAddress => entry !== null)
}

/**
 * Lowercased addresses from a stored list, de-duplicated and order-preserving.
 * The common case for anything that cares about who receives a message.
 */
export const parseAddresses = (value: string | null | undefined): string[] => {
  const seen = new Set<string>()

  for (const { address } of parseAddressList(value)) {
    seen.add(address.toLowerCase())
  }

  return Array.from(seen)
}

/** Render one address, quoting the display name when it contains specials. */
export const formatAddress = ({ name, address }: EmailAddress): string => {
  if (!name) return address

  const safeName = NAME_NEEDS_QUOTING.test(name) ? `"${name.replace(/(["\\])/g, '\\$1')}"` : name

  return `${safeName} <${address}>`
}

/**
 * Human-readable label for one address, without RFC quoting — `Masheder, Scott
 * <scott@apex.co.uk>` rather than the quoted form. For display only: the entries of
 * a parsed list are already separated, so nothing re-splits them. Use `formatAddress`
 * for anything that will be stored or sent.
 */
export const formatAddressDisplay = ({ name, address }: EmailAddress): string =>
  name ? `${name} <${address}>` : address

/**
 * Render an address list for storage. Quoting here is what keeps a name like
 * "Masheder, Scott" from splitting into two bogus entries when it is read back.
 */
export const formatAddressList = (addresses: (EmailAddress | string)[]): string =>
  addresses
    .map((entry) => (typeof entry === 'string' ? entry.trim() : formatAddress(entry)))
    .filter(Boolean)
    .join(SEPARATOR)

/**
 * Re-join a list of raw address strings using the canonical delimiter, parsing
 * each one first so a legacy comma-delimited value is normalised on the way in.
 */
export const formatAddressStrings = (values: (string | null | undefined)[]): string =>
  formatAddressList(values.flatMap((value) => parseAddressList(value)))

/**
 * Whether two addresses are the same person, ignoring case, surrounding display names
 * and the local snapshot's `.invalid` suffix — so a comparison written here still holds
 * on a developer machine, where one side may carry the suffix and the other may not.
 */
export const isSameAddress = (a: string | null | undefined, b: string | null | undefined): boolean => {
  const read = (value: string | null | undefined) => {
    const [parsed] = parseAddressList(value)

    return forComparison(parsed?.address ?? value ?? '')
  }

  const left = read(a)

  return Boolean(left) && left === read(b)
}

const CREWSADERS_DOMAIN = '@crewsaders.com'

/**
 * Whether an address is one of ours. Takes a raw token or a bare address, and
 * matches on the domain rather than a substring, so a lookalike domain such as
 * `bookings@crewsaders.com.example.net` is correctly treated as external.
 */
export const isCrewsadersAddress = (value: string | null | undefined): boolean => {
  if (!value) return false

  const [parsed] = parseAddressList(value)
  const address = parsed?.address ?? value

  return forComparison(address).endsWith(CREWSADERS_DOMAIN)
}

/**
 * Where an address sits relative to the conversation:
 *
 * - `internal` — one of ours.
 * - `known` — a domain already recognised on this thread, e.g. the client we are
 *   talking to or a contact on the linked job.
 * - `external` — anything else. Not necessarily wrong, but it is the case worth
 *   showing someone before they hit reply: it is how a competitor CC'd on a booking
 *   enquiry nearly received a reply with job details attached.
 */
export type AddressOrigin = 'internal' | 'known' | 'external'

/** The domain of an address, lowercased, or null when there isn't one. */
export const addressDomain = (value: string | null | undefined): string | null => {
  const [parsed] = parseAddressList(value)
  const address = parsed?.address ?? value?.trim()

  const at = address?.lastIndexOf('@') ?? -1
  if (!address || at < 1 || at === address.length - 1) return null

  return forComparison(address.slice(at + 1)) || null
}

/**
 * Classify an address against the domains we already recognise on a thread. Pass the
 * client's and the linked job's contact domains as `knownDomains`; ours is implicit.
 *
 * An array rather than an Iterable: this package is consumed by apps that inherit
 * `target: es5` from packages/tsconfig/nextjs.json, where iterating a generic Iterable
 * needs downlevelIteration. Arrays are special-cased by the compiler and are what every
 * caller passes anyway.
 */
export const classifyAddress = (
  value: string | null | undefined,
  knownDomains: readonly string[] = []
): AddressOrigin => {
  if (isCrewsadersAddress(value)) return 'internal'

  const domain = addressDomain(value)
  if (!domain) return 'external'

  return knownDomains.some((known) => known && known.trim().toLowerCase() === domain) ? 'known' : 'external'
}

/** Collect the distinct domains of a set of addresses, skipping ours. */
export const collectKnownDomains = (values: (string | null | undefined)[]): string[] => {
  const domains = new Set<string>()

  for (const value of values) {
    if (isCrewsadersAddress(value)) continue

    const domain = addressDomain(value)
    if (domain) domains.add(domain)
  }

  return Array.from(domains)
}
